import { z } from 'zod';
import type { RefundReason } from '@/lib/api-admin/types';

const REFUND_REASONS = [
  'CHANGE_OF_MIND',
  'WRONG_OPTION',
  'DELIVERY_ISSUE',
  'PRODUCT_DEFECT',
  'DUPLICATE_PAYMENT',
  'OPERATOR_ADJUSTMENT',
  'OTHER',
] as const satisfies ReadonlyArray<RefundReason>;

export const REFUND_REASON_OPTIONS: ReadonlyArray<RefundReason> = REFUND_REASONS;

/**
 * 환불 계좌(가상계좌 결제 환불 필수 — 없으면 BE 400 PAYMENT_REFUND_ACCOUNT_REQUIRED). 입력한 계좌로 Toss 가 고객에게
 * 송금한다. 은행 코드는 금융결제원 3자리(KR_BANKS), 계좌번호는 하이픈·공백이 섞여도 되고 보낼 때 숫자만 남긴다.
 */
export const refundAccountSchema = z.object({
  bankCode: z.string().trim().regex(/^\d{3}$/),
  accountNumber: z
    .string()
    .trim()
    .max(32)
    .refine((v) => /^[\d\s-]+$/.test(v) && /\d/.test(v)),
  holderName: z.string().trim().min(1).max(64),
});

export type RefundAccountInput = z.infer<typeof refundAccountSchema>;

export interface RefundSchemaContext {
  /** [pay-currency] 청구통화 **major** 단위(BE RefundCreateRequest.amount 계약 — KRW/JPY dp=0). */
  remainingRefundableAmount: number;
  requireRefundAccount: boolean;
  /**
   * [pay-currency] 통화 소수 자릿수(USD/CNY=2, KRW/JPY=0). 초과 정밀도 입력을 FE 에서 차단해
   * BE HALF_UP 반올림이 운영자 의도와 다른 금액을 환불하는 일을 막는다. 미지정=0(정수, 기존 동작).
   */
  maxDecimalPlaces?: number;
}

/** 소수 자릿수 검사 — float 곱셈 함정 회피를 위해 문자열 기반(지수 표기는 불허). */
const hasAtMostDecimals = (value: number, maxDp: number): boolean => {
  const s = String(value);
  if (s.includes('e') || s.includes('E')) return false;
  const frac = s.split('.')[1] ?? '';
  return frac.length <= maxDp;
};

export const createRefundFormSchema = (ctx: RefundSchemaContext) =>
  z
    .object({
      amount: z
        .number({ invalid_type_error: 'amount.invalid' })
        .positive('amount.positive')
        .max(ctx.remainingRefundableAmount, 'amount.exceeds_refundable')
        .refine((v) => hasAtMostDecimals(v, ctx.maxDecimalPlaces ?? 0), 'amount.precision'),
      reason: z.enum(REFUND_REASONS),
      detail: z.string().trim().max(500).optional().or(z.literal('')),
      refundAccount: refundAccountSchema.optional().or(z.null()),
    })
    .superRefine((value, ctx2) => {
      if (ctx.requireRefundAccount && !value.refundAccount) {
        ctx2.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['refundAccount'],
          message: 'refund_account.required',
        });
      }
    });

export type RefundFormValues = z.input<ReturnType<typeof createRefundFormSchema>>;

/**
 * 수기 환불 방법(be-payment V20 ManualRefundMethod). PG 를 부르지 않고 에버라인 밖에서 처리한 환불을 기록한다.
 *   ORIGINAL_PG   원 결제 PG 관리자 화면에서 취소(이관 LEGACY·수기주문)
 *   BANK_TRANSFER 고객 계좌로 직접 이체 — 환불 계좌 필수
 *   OTHER         그 외(예: 적립금 대체) — 상세 사유에 기록
 * Toss 가상계좌 결제는 BANK_TRANSFER 만 된다(그 외는 409 PAYMENT_METHOD_NOT_SUPPORTED).
 */
export const MANUAL_REFUND_METHODS = ['ORIGINAL_PG', 'BANK_TRANSFER', 'OTHER'] as const;
export type ManualRefundMethod = (typeof MANUAL_REFUND_METHODS)[number];

export const createManualRefundFormSchema = (ctx: Omit<RefundSchemaContext, 'requireRefundAccount'>) =>
  z
    .object({
      amount: z
        .number({ invalid_type_error: 'amount.invalid' })
        .positive('amount.positive')
        .max(ctx.remainingRefundableAmount, 'amount.exceeds_refundable')
        .refine((v) => hasAtMostDecimals(v, ctx.maxDecimalPlaces ?? 0), 'amount.precision'),
      reason: z.enum(REFUND_REASONS),
      // BE ManualRefundRequest.detail @Size(max = 255).
      detail: z.string().trim().max(255).optional().or(z.literal('')),
      method: z.enum(MANUAL_REFUND_METHODS),
      // 계좌 입력칸은 폼에 늘 있고(계좌이체가 아니면 숨김), 계좌이체일 때만 검사한다.
      refundAccount: z
        .object({ bankCode: z.string(), accountNumber: z.string(), holderName: z.string() })
        .nullable()
        .optional(),
      /** 실제 환불 처리 일시(datetime-local). 비우면 기록 시각. */
      processedAt: z.string().optional().or(z.literal('')),
    })
    .superRefine((value, issue) => {
      if (value.method === 'BANK_TRANSFER' && !refundAccountSchema.safeParse(value.refundAccount).success) {
        issue.addIssue({ code: z.ZodIssueCode.custom, path: ['refundAccount'], message: 'refund_account.required' });
      }
      // 실제 처리 일시는 미래일 수 없다(BE 400 COMMON_VALIDATION_FAILED).
      if (value.processedAt) {
        const at = new Date(value.processedAt);
        if (Number.isNaN(at.getTime()) || at.getTime() > Date.now()) {
          issue.addIssue({ code: z.ZodIssueCode.custom, path: ['processedAt'], message: 'processed_at.future' });
        }
      }
    });

export type ManualRefundFormValues = z.input<ReturnType<typeof createManualRefundFormSchema>>;

export const voidFormSchema = z.object({
  reason: z.string().trim().min(1, 'reason.required').max(200),
  detail: z.string().trim().max(500).optional().or(z.literal('')),
});

export type VoidFormValues = z.input<typeof voidFormSchema>;
